import { usePathname, useRouter } from 'expo-router'
import { createContext, useContext, useEffect, useState, type PropsWithChildren, type ReactNode } from 'react'

import { env } from '@/src/config/env'
import { ProfileUserModal } from '@/src/elements/user-profile/UserProfilePreviewWithModal/ProfileUserModal/ProfileUserModal'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import type { TMediumUserApi } from '@/src/features/user/api/userApi.types'
import { useUserQuery } from '@/src/features/user/services/useUserQuery'
import { useSearchParamsAction } from '@/src/general/hooks/useSearchParamsAction'

import { useOnChange } from '../../hooks/useOnChange'
import { useRoutes } from '../../utils/routes'

type TUserProfileContext = {
  showUserProfileModal: boolean
  openUserProfileModal: (userToShow: TMediumUserApi) => void
}

const UserProfileWithModal = createContext<TUserProfileContext>({
  showUserProfileModal: false,
  openUserProfileModal: () => {
    throw new Error(
      'Cannot set show user profile: user profile context is not provided. Seems you forgot to wrap component within <UserProfileModalProvider />',
    )
  },
})

/**
 * UserProfileWithModal Provider
 *
 * Provides the user profile modal display state
 *
 * 2 ways to define or recognize the user profile to display:
 *  - by providing directly the user props in the openUserProfileModal function
 *  - by providing the user id in the url
 */
export const UserProfileModalProvider = ({
  children,
  searchParams,
}: PropsWithChildren<TSearchParamsParam>): ReactNode => {
  const [userId, setUserId] = useState<number | undefined>(undefined)
  const [user, setUser] = useState<TMediumUserApi | undefined>(undefined)
  const [showModal, setShowModal] = useState(false)
  const pathname = usePathname()
  const { navigate } = useRouter()
  const { routes } = useRoutes()

  const { data: me } = useMeQuery()
  const isLoggedIn = getIsLoggedIn()

  useSearchParamsAction({
    searchParams,
    action: ({ userid }) => {
      if (isLoggedIn) {
        setUserId(Number(userid))
      } else {
        navigate(routes.connect(`${env.EXPO_PUBLIC_WEB_BASE_URL}${pathname}?action=show-user&userid=${String(userid)}`))
      }
    },
    requestedQuery: { action: 'show-user', userid: (value) => !!Number(value) },
    keepQueryParams: true,
  })

  const isNotMe = userId && me && me.id !== userId
  const { data: userInfos } = useUserQuery(isNotMe ? userId : undefined)

  useOnChange(userInfos, () => {
    if (userInfos) setUser(userInfos)
  })
  useOnChange(userInfos?.id, () => {
    if (userInfos) setShowModal(true)
  })

  const openUserProfileModal = (userToShow: TMediumUserApi) => {
    setUserId(undefined)
    setUser(userToShow)
    setShowModal(true)
  }

  const dismissUserProfileModal = () => {
    setShowModal(false)
    setUserId(undefined)
  }

  useEffect(() => {
    if (me?.id && (me.id === user?.id || me.id === userId)) {
      navigate(routes.settings())
    }
  }, [user, userId, routes, navigate, me])

  const value = {
    showUserProfileModal: showModal,
    openUserProfileModal,
  }

  return (
    <UserProfileWithModal.Provider value={value}>
      {user && (
        <ProfileUserModal
          showUserProfileModal={showModal}
          dismissUserProfileModal={dismissUserProfileModal}
          user={user}
        />
      )}
      {children}
    </UserProfileWithModal.Provider>
  )
}

export const useUserProfileModalContext = () => useContext(UserProfileWithModal)
